
import { useState } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { tarifaEditSchema, TarifaEditValues } from "./form/types";
import { Cliente, Gerador } from "@/types";
import { CircleDollarSign, TrendingUp, TrendingDown, Percent, Check } from "lucide-react";
import { ClienteService } from "@/services/StorageService";
import { toast } from "sonner";
import * as z from "zod";

// Extended schema to include generator rate
const tarifaCompleteSchema = tarifaEditSchema.extend({
  tarifaGerador: z.number().optional()
});

// Extended type for the form
interface TarifaCompleteValues extends TarifaEditValues {
  tarifaGerador?: number;
}

interface TarifaEdicaoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  cliente: Cliente;
  gerador: Gerador | null;
  onTarifaUpdated: (cliente: Cliente) => void;
}

export const TarifaEdicaoDialog = ({ 
  isOpen, 
  onClose, 
  cliente, 
  gerador,
  onTarifaUpdated
}: TarifaEdicaoDialogProps) => {
  const [loading, setLoading] = useState(false);
  
  // Get client-specific generator rate if available
  const tarifaGeradorAtual = cliente.tarifaGerador !== undefined 
    ? cliente.tarifaGerador 
    : gerador?.tarifaVenda || 0;
  
  const form = useForm<TarifaCompleteValues>({
    resolver: zodResolver(tarifaCompleteSchema),
    defaultValues: {
      tarifaEnergia: cliente.tarifaEnergia,
      tarifaGerador: tarifaGeradorAtual
    }
  });
  
  const tarifaAtual = cliente.tarifaEnergia;
  const tarifaGerador = form.watch("tarifaGerador") || tarifaGeradorAtual;
  const consumoMedio = cliente.gastoEnergia / cliente.tarifaEnergia;
  
  const novaTarifa = form.watch("tarifaEnergia");
  const novaEconomia = consumoMedio * (novaTarifa - tarifaGerador);
  const economiaAtual = consumoMedio * (tarifaAtual - tarifaGeradorAtual);
  
  const percentualEconomia = cliente.gastoEnergia > 0 
    ? (novaEconomia / cliente.gastoEnergia) * 100 
    : 0;
  
  const diferencaEconomia = novaEconomia - economiaAtual;
  const impactoPercentual = economiaAtual !== 0 
    ? (diferencaEconomia / economiaAtual) * 100 
    : 0;
  
  const onSubmit = async (values: TarifaCompleteValues) => {
    setLoading(true);
    try {
      // Only include tarifaGerador if it's different from the generator's default
      const updateData: any = {
        ...cliente,
        tarifaEnergia: values.tarifaEnergia
      };
      
      // Only set custom generator rate if it's different from the default
      if (values.tarifaGerador !== undefined && 
          gerador && 
          values.tarifaGerador !== gerador.tarifaVenda) {
        updateData.tarifaGerador = values.tarifaGerador;
      } else if (values.tarifaGerador === gerador?.tarifaVenda) {
        // If the value is the same as the default, remove any custom setting
        updateData.tarifaGerador = undefined;
      }
      
      const clienteAtualizado = ClienteService.update(cliente.id, updateData);
      
      if (clienteAtualizado) {
        toast.success("Tarifas atualizadas com sucesso!");
        onTarifaUpdated(clienteAtualizado);
        onClose();
      } else {
        toast.error("Erro ao atualizar tarifas");
      }
    } catch (error) {
      console.error("Erro ao atualizar tarifas:", error);
      toast.error("Erro ao atualizar tarifas");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-700">
            <CircleDollarSign className="h-5 w-5" />
            Editar Tarifas
          </DialogTitle>
          <DialogDescription>
            Ajuste as tarifas para o cliente <span className="font-medium">{cliente.nome}</span>
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="space-y-4 py-4">
              <FormField
                control={form.control}
                name="tarifaEnergia"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tarifa de Energia do Cliente (R$/kWh)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        step="0.0001" 
                        min="0"
                        placeholder="0.0000" 
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="tarifaGerador"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Tarifa do Gerador para este Cliente (R$/kWh)
                      {gerador && field.value === gerador.tarifaVenda && (
                        <span className="text-xs ml-2 text-muted-foreground">(Padrão do gerador)</span>
                      )}
                    </FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        step="0.0001" 
                        min="0"
                        placeholder="0.0000" 
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {gerador && (
                <div className="bg-blue-50 p-4 rounded-md space-y-3 mt-4">
                  <h3 className="text-sm font-medium text-blue-800">Análise de Impacto</h3>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white p-3 rounded-md shadow-sm">
                      <p className="text-xs text-muted-foreground">Consumo Médio</p>
                      <p className="font-medium text-blue-900">{consumoMedio.toFixed(2)} kWh/mês</p>
                    </div>
                    
                    <div className="bg-white p-3 rounded-md shadow-sm">
                      <p className="text-xs text-muted-foreground">Tarifa Padrão do Gerador</p>
                      <p className="font-medium text-blue-900">R$ {gerador.tarifaVenda.toFixed(4)}/kWh</p>
                    </div>
                  </div>
                  
                  <div className="bg-white p-3 rounded-md shadow-sm">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs text-muted-foreground">Nova Economia Estimada</p>
                        <p className="font-medium text-blue-900">R$ {novaEconomia.toFixed(2)}/mês</p>
                      </div>
                      <div className={`flex items-center ${diferencaEconomia >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {diferencaEconomia >= 0 ? (
                          <TrendingUp className="h-4 w-4 mr-1" />
                        ) : (
                          <TrendingDown className="h-4 w-4 mr-1" />
                        )}
                        <span className="text-sm font-medium">
                          {diferencaEconomia >= 0 ? '+' : ''}{diferencaEconomia.toFixed(2)} R$
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-3 rounded-md shadow-sm">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs text-muted-foreground">Percentual de Economia</p>
                        <p className="font-medium text-blue-900">{percentualEconomia.toFixed(2)}%</p>
                      </div>
                      <div className={`flex items-center ${impactoPercentual >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        <Percent className="h-4 w-4 mr-1" />
                        <span className="text-sm font-medium">
                          {impactoPercentual >= 0 ? '+' : ''}{impactoPercentual.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? 'Salvando...' : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Salvar Alterações
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
